import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../api';
import { API_ENDPOINTS } from '../config';
import { useI18n } from '../i18n';
import './Auth.css';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    teamName: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { t, basePath } = useI18n();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    if (!formData.username || !formData.email || !formData.teamName || 
        !formData.password || !formData.confirmPassword) {
      setError(t('auth.error.required'));
      return false;
    }
    
    if (formData.password.length < 6) {
      setError(t('auth.error.passwordLen'));
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.error.passwordMatch'));
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('auth.error.email'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await apiRequest(API_ENDPOINTS.register, {
        method: 'POST',
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password
        })
      });

      setSuccess(t('auth.success.register'));
      setLoading(false);
      setTimeout(() => navigate(`${basePath}/login`), 800);
    } catch (err) {
      setError(err.message || t('auth.error.registerFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="bg-gradient"></div>
        <div className="bg-grid"></div>
      </div>
      
      <div className="auth-card register-card">
        <div className="auth-header">
          
          <h1 className="auth-title">{t('auth.register.title')}</h1>
          <p className="auth-subtitle">{t('auth.register.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
          {success && (
            <div className="auth-success">
              <span className="success-icon">✅</span>
              {success}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username">{t('auth.username')}</label>
              <div className="input-wrapper">
                <span className="input-icon">👤</span>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t('auth.username')}
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="teamName">{t('auth.team')}</label>
              <div className="input-wrapper">
                <span className="input-icon">🏢</span>
                <input
                  type="text"
                  id="teamName"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  placeholder={t('auth.team')}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <div className="input-wrapper">
              <span className="input-icon">📧</span>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.email')}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('auth.password')}</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('auth.password')}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.confirm')}</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('auth.confirm')}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          <div className="form-options">
            <label className="checkbox-label">
              <input type="checkbox" required />
              <span className="checkmark"></span>
              {t('auth.agree')}<a href="#terms">{t('auth.terms')}</a>
            </label>
          </div>

          <button 
            type="submit" 
            className={`auth-submit ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                {t('auth.register.loading')}
              </>
            ) : (
              t('auth.register')
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>{t('auth.hasAccount')}</p>
          <Link to={`${basePath}/leaderboard/login`} className="auth-link">{t('auth.toLogin')}</Link>
        </div>

        <Link to={`${basePath}/leaderboard`} className="back-home">
          ← {t('auth.backHome')}
        </Link>
      </div>
    </div>
  );
};

export default Register;
